import { useEffect, useMemo, useRef, useState } from "react";
import { GlassCard, Button, StatusPill } from "@/shared/ui";
import { useAuthConfig, useDetect } from "@/shared/api/hooks";
import { useSettings } from "@/shared/api/settings";
import { useI18n } from "@/shared/i18n";
import { filterEnglishOnly } from "@/shared/text";
import { ApiError } from "@/shared/api/client";
import type { DetectResponse, DetectSentence } from "@/shared/api/schemas";
import { DetectBadge, providerLabel, toneOf } from "./DetectBadge";
import { detectCost } from "./pricing";
import "./detect.css";

// Same limits as the humanizer so text round-trips between the two tabs.
const MAX_WORDS = 600;
const MAX_CHARS = 5000;

function countWords(s: string): number {
  const t = s.trim();
  return t ? t.split(/\s+/).length : 0;
}

/** Sentence-level bucket for the highlight; coarser than the document tone. */
function sentenceTone(s: DetectSentence): "hi" | "mid" | "lo" {
  if (s.flagged || s.ai_prob >= 0.7) return "hi";
  if (s.ai_prob >= 0.4) return "mid";
  return "lo";
}

function SentenceBreakdown({ result }: { result: DetectResponse }) {
  const { t } = useI18n();
  if (!result.sentences.length) return null;
  return (
    <div className="dt-sentences">
      <div className="dt-sentences__head">
        <span className="dt-sentences__title">{t("Sentence breakdown")}</span>
        <span className="dt-legend">
          <span className="dt-legend__item dt-legend__item--hi">{t("Likely AI")}</span>
          <span className="dt-legend__item dt-legend__item--mid">{t("Uncertain")}</span>
          <span className="dt-legend__item dt-legend__item--lo">{t("Likely human")}</span>
        </span>
      </div>
      <p className="dt-sentences__body">
        {result.sentences.map((s, i) => (
          <span
            key={i}
            className={`dt-sent dt-sent--${sentenceTone(s)}`}
            title={`AI ${Math.round(s.ai_prob * 100)}%`}
          >
            {s.text}{" "}
          </span>
        ))}
      </p>
    </div>
  );
}

interface DetectPageProps {
  /** Hand the checked text to the humanizer tab. */
  onHumanize: (text: string) => void;
  /** Navigate to the Account tab (sign up / sign in lives there). */
  onGoAccount?: () => void;
}

export function DetectPage({ onHumanize, onGoAccount }: DetectPageProps) {
  const { hasKey, settings } = useSettings();
  const { t } = useI18n();
  const provider = useAuthConfig().data?.detect_provider;
  const label = provider ? providerLabel(provider) : t("AI detector");
  const detect = useDetect();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [filtered, setFiltered] = useState(false);
  const previousAuth = useRef({ apiBase: settings.apiBase, credential: settings.jwt || settings.apiKey });
  useEffect(() => {
    const credential = settings.jwt || settings.apiKey;
    const previous = previousAuth.current;
    if ((previous.credential && previous.credential !== credential) || previous.apiBase !== settings.apiBase) {
      setText("");
      setFiltered(false);
      setError(null);
    }
    previousAuth.current = { apiBase: settings.apiBase, credential };
  }, [settings.apiBase, settings.jwt, settings.apiKey]);
  // The text the current result belongs to; edits after scoring make it stale.
  const [checkedText, setCheckedText] = useState<string | null>(null);

  const words = useMemo(() => countWords(text), [text]);
  const overWords = words > MAX_WORDS;
  const overChars = text.length > MAX_CHARS;
  const over = overWords || overChars;
  const cost = detectCost(words);
  const result = detect.data ?? null;
  const stale = result != null && checkedText !== text;
  const canRun = !over && !detect.isPending && !!provider;

  // Preserve the full source and report unsupported characters.
  function onChangeText(raw: string) {
    const { removed } = filterEnglishOnly(raw);
    setText(raw);
    setFiltered(removed > 0);
  }

  async function run() {
    setError(null);
    if (filtered) {
      setError(t("Unsupported characters found. Edit the source before continuing; your original text is preserved."));
      return;
    }
    if (!hasKey) {
      setError(t("Sign up or sign in first — every account gets free words each month."));
      return;
    }
    if (!text.trim()) {
      setError(t("Paste some English text first."));
      return;
    }
    try {
      await detect.mutateAsync(text);
      setCheckedText(text);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("Detection failed, please retry"));
    }
  }

  const tone = result ? toneOf(result) : null;

  return (
    <>
      <div className="page__head">
        <h1 className="page__title" style={{ display: "flex", alignItems: "center", gap: "var(--s3)" }}>
          {t("AI Detector")}
          {provider ? <StatusPill tone="accent">{label}</StatusPill> : null}
        </h1>
        <p className="page__sub">
          {t(
            "Paste any English text and see how an AI detector scores it, sentence by sentence. If it reads as AI, one click sends it to the humanizer.",
          )}
        </p>
      </div>

      <div className="dt-grid">
        {/* Input */}
        <GlassCard title={t("Text to check")}>
          <textarea
            className="textarea"
            placeholder={t("Paste the English text to check…")}
            value={text}
            onChange={(e) => onChangeText(e.target.value)}
          />
          <div className="dt-counter" style={over ? { color: "var(--danger)" } : undefined}>
            {words} / {MAX_WORDS} words
            <span style={{ opacity: 0.6 }}> · {text.length} chars</span>
            {overWords && <span> — {t("over the {n}-word limit", { n: MAX_WORDS })}</span>}
            {!overWords && overChars && (
              <span> — {t("over the {n}-char limit", { n: MAX_CHARS })}</span>
            )}
          </div>
          {filtered && (
            <div className="dt-error" style={{ color: "var(--warning)" }}>
              <span aria-hidden>ⓘ</span>
              {t("Unsupported characters found. Edit the source before continuing; your original text is preserved.")}
            </div>
          )}

          <div style={{ marginTop: "var(--s3)" }}>
            <Button onClick={run} loading={detect.isPending} disabled={!canRun} block>
              {detect.isPending
                ? t("Checking…")
                : t("Check with {p} · ~{n} words", { p: label, n: cost })}
            </Button>
          </div>
          {!provider && (
            <div className="dt-error">
              <span aria-hidden>⚠</span>
              {t("Detection is not configured on this server.")}
            </div>
          )}
          {error && (
            <div className="dt-error">
              <span aria-hidden>⚠</span>
              {error}
            </div>
          )}
          {!hasKey && (
            <div className="dt-signup">
              <div className="dt-signup__text">
                {t("New here? Sign up in a minute — 500 free words every month.")}
              </div>
              <Button variant="ghost" onClick={onGoAccount}>
                {t("Sign up / Sign in")}
              </Button>
            </div>
          )}
        </GlassCard>

        {/* Result */}
        <GlassCard
          title={t("Detection result")}
          action={
            result ? (
              <StatusPill tone={tone === "human" ? "ok" : tone === "mixed" ? "warn" : "error"}>
                {t("Verdict: {v}", { v: result.verdict })}
              </StatusPill>
            ) : undefined
          }
        >
          {result && checkedText ? (
            <div className="dt-result">
              <DetectBadge result={result} text={checkedText} />
              {stale && (
                <div className="dt-error" style={{ color: "var(--warning)" }}>
                  <span aria-hidden>ⓘ</span>
                  {t("The text changed since this score — check again for a current result.")}
                </div>
              )}
              <SentenceBreakdown result={result} />
              <div className="dt-cta">
                <Button
                  variant={tone === "human" ? "ghost" : "primary"}
                  onClick={() => onHumanize(checkedText)}
                >
                  {tone === "human"
                    ? t("Already reads as human — humanize anyway")
                    : t("Humanize this text →")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="dt-empty">
              {t("Score the text on the left to see the result here.")}
            </div>
          )}

          <div className="dt-notes">
            <p>
              {t(
                "Detector scores are a snapshot: detectors retrain, so a result carries the date it was taken.",
              )}
            </p>
            <p>
              {t(
                "Turnitin's AI report is visible only to instructors. No third-party tool can check it — including any that put the name on a button.",
              )}
            </p>
          </div>
        </GlassCard>
      </div>
    </>
  );
}
